import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { IndicationDefinition, IndicationGroup } from '@app/models/indication';
import { InputOption } from '@app/models/input-option';
import { ControlTypeEnum, SelectionsModel } from '@app/models/selections-model';
import { SharedService } from '@app/services/shared.service';
import { vennHealthConstants } from '@app/shared/vennHealthConstants';
import { VennHealthHelpers } from '@app/shared/vennHealthHelpers';
import { asapScheduler, first, skip } from 'rxjs';

@Component({
  selector: 'app-limits-input',
  templateUrl: './limits-input.component.html',
  styleUrls: ['./limits-input.component.css']
})
export class LimitsInputComponent implements OnInit {

  @Input() indication: IndicationDefinition;
  @Input() labelName: string = 'Limits:';
  @Input() hasCustomControlParent: boolean = false;

  @Output() indicationSelectionChanged: EventEmitter<SelectionsModel[]> = new EventEmitter();

  allIndicationDefinitions: IndicationGroup[] = vennHealthConstants.allIndications;
  allIndications: IndicationGroup[] = [];

  firstTime = true;

  public formGroup: FormGroup;

  constructor (
    private formBuilder: FormBuilder, 
    private sharedService: SharedService
  ) {}

  ngOnInit(): void {
    // only called first time to setup form and update selected filters via shared service
    this.sharedService.allIndications.pipe(first()).subscribe(data => {
      if (data) {
        this.allIndications = data;
        this.setInitialForm();
      }
    });

    // called everytime after the first time
    this.sharedService.allIndications.pipe(skip(1)).subscribe(data => {
      if (data) {
        this.allIndications = data;
      }
    });

    this.sharedService.activeSavedFilters.subscribe(data => {
      if (data && !this.hasCustomControlParent) {
        // this.allIndications = data.indicationGroups;
        const loadedIndication = VennHealthHelpers.getCurrentIndicationFromIndicationGroupJson(this.indication, this.allIndications);
        this.indication.selections = loadedIndication.selections;
        this.indication.selected = loadedIndication.selected;

        this.setIndicationSelections();
      }
    });
  }

  setInitialForm(): void {
    let form = {};
    for(let i = 0; i < this.indication.inputOptions.length; i++) {
      form[this.indication.inputOptions[i].name] = new FormControl(this.indication.inputOptions[i].defaultValue, [
        Validators.required,
        Validators.minLength(1),
        Validators.pattern(vennHealthConstants.regexPatterns.numericWithDecimal)
      ]);
    }
    this.formGroup = this.formBuilder.group(form);

    this.labelName = `Limits: ${this.indication.displayName}`;
    this.setIndicationSelections();
  }

  setIndicationSelections() {
    const indicationControlSpecificSelections = this.indication.selections.filter(i => i.controlType === ControlTypeEnum.TEXT);
    if (this.indication.selected && indicationControlSpecificSelections.length > 0) {
      asapScheduler.schedule(() => {
        for(let i = 0; i < this.indication.inputOptions.length; i++) {
          this.formGroup.controls[indicationControlSpecificSelections[i].controlName].setValue(indicationControlSpecificSelections[i].selectionValue);
        }
        this.updateSelections();
      });
    } else {
      this.setDefaults();
    }
  }

  setDefaults() {
    for(let i = 0; i < this.indication.inputOptions.length; i++) {
      this.formGroup.controls[this.indication.inputOptions[i].name].setValue(this.indication.inputOptions[i].defaultValue);
    }
    this.updateSelections();
  }

  overrideInputValue(controlName, value) {
    this.formGroup.controls[controlName].setValue(value);
    this.updateSelections();
  }

  getInputLabel(inputOption: InputOption, units?: string): string {
    let label = inputOption.displayName;
    if (inputOption.hasOwnProperty('details')) {
      label += ' ' + inputOption.details;
    } 
    if (units) {
      label += ' (' + units + ')';
    }
    return label;
  }

  updateSelections(): void {
    const currentSelections: SelectionsModel[] = [];
    Object.keys(this.formGroup.controls).forEach(key => {
      const control = this.formGroup.get(key);
      const option = this.indication.inputOptions.find(i => i.name === key);
      const currentSelection: SelectionsModel = {
        indicationName: this.indication.name,
        controlName: key,
        controlType: ControlTypeEnum.TEXT,
        controlDisplayName: option.displayName,
        order: option.order,
        selectionValue: control.value,
        selectionDisplayName: option.displayName 
      };
      currentSelections.push(currentSelection);
    });
    
    if (this.hasCustomControlParent) {
      this.indicationSelectionChanged.emit(currentSelections);
    } else {
      this.indication.selections = currentSelections;
      const currentIndicationGroup = VennHealthHelpers.getIndicationGroup(this.indication);
      const matchingGroupIndex = this.allIndications.findIndex(i => i.name === currentIndicationGroup.name);
      const matchingIndicationIndex = this.allIndications[matchingGroupIndex].indications.findIndex(i => i.name === this.indication.name);
      this.allIndications[matchingGroupIndex].indications[matchingIndicationIndex] = this.indication;
      this.sharedService.setAllIndications(this.allIndications);
    }
  }
}
